import type { ButtonHTMLAttributes, ReactNode } from 'react'

import { confirmDialog } from '@/components/ui/ConfirmDialog'

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> {
  variant?: ButtonVariant
  loading?: boolean
  icon?: ReactNode
  confirmTitle?: string
  confirmText?: string
  skipConfirm?: boolean
  onClick?: () => void | Promise<void>
}

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  ghost: 'btn-light',
  danger: 'btn-danger',
}

/**
 * Danger-variant buttons confirm before executing by default (Frontend Spec
 * §5.1) — pass skipConfirm when the caller already confirms elsewhere (e.g.
 * a confirmation happens inside a modal this button submits).
 */
export function Button({
  variant = 'primary',
  loading = false,
  icon,
  confirmTitle,
  confirmText,
  skipConfirm = false,
  className = '',
  children,
  disabled,
  onClick,
  ...rest
}: ButtonProps) {
  const handleClick = async () => {
    if (!onClick) return

    if (variant === 'danger' && !skipConfirm) {
      const confirmed = await confirmDialog({
        title: confirmTitle ?? 'Are you sure?',
        text: confirmText,
        confirmVariant: 'danger',
        confirmLabel: 'Yes, continue',
      })
      if (!confirmed) return
    }

    await onClick()
  }

  return (
    <button
      type="button"
      className={`btn ${VARIANT_CLASSES[variant]} waves-effect waves-light ${className}`.trim()}
      disabled={disabled || loading}
      onClick={() => void handleClick()}
      {...rest}
    >
      {loading ? (
        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
      ) : (
        icon && <span className="me-2">{icon}</span>
      )}
      {children}
    </button>
  )
}
